import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Github, ExternalLink, Video, Share2, Medal } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const medal = ['bg-[#F59E0B]', 'bg-[#94A3B8]', 'bg-[#B45309]'];
const img = (u) => (u?.startsWith('/api') ? `${API}${u}` : u);

const Links = ({ p }) => (
  <div className="flex gap-3 mt-3 text-xs font-semibold">
    {p.demo_url && <a href={p.demo_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#002FA7]"><ExternalLink size={12} /> Demo</a>}
    {p.github_url && <a href={p.github_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0A0A0A]"><Github size={12} /> Code</a>}
    {p.video_url && <a href={p.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#52525B]"><Video size={12} /> Video</a>}
  </div>
);

const WinnerCard = ({ w, i }) => {
  const p = w.team.project || {};
  return (
    <div className="bg-white border-2 border-[#0A0A0A] rounded-lg overflow-hidden" data-testid={`showcase-winner-${w.team_id}`}>
      {p.image_url && <img src={img(p.image_url)} alt={p.title} className="w-full h-36 object-cover" />}
      <div className="p-5">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-[0.06em] text-white ${medal[i] || 'bg-[#002FA7]'}`}><Medal size={12} /> {w.label}</span>
        <h3 className="text-lg font-extrabold text-[#0A0A0A] mt-2">{p.title || w.team.name}</h3>
        <p className="text-xs text-[#52525B] mt-0.5">Team {w.team.name} · {w.team.members.map((m) => m.name.split(' ')[0]).join(', ')}</p>
        {p.description && <p className="text-sm text-[#52525B] mt-2 line-clamp-3">{p.description}</p>}
        <Links p={p} />
      </div>
    </div>
  );
};

// Public winners + projects section for a finished hackathon
export const HackathonShowcase = ({ eventId, title }) => {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/api/event-teams/public/${eventId}/showcase`).then((r) => setData(r.data)).catch(() => {}); }, [eventId]);
  if (!data?.published) return null;
  const share = async () => {
    const url = `${window.location.origin}/events/${eventId}#showcase`;
    const top = data.winners[0];
    const text = top ? `${top.team.name} won ${top.label} at ${title}! See all winning projects on EvalentX` : `Winning projects from ${title} on EvalentX`;
    if (navigator.share) { try { await navigator.share({ title, text, url }); } catch { /* cancelled */ } }
    else {
      try { await navigator.clipboard.writeText(`${text} ${url}`); toast.success('Showcase link copied'); }
      catch { window.prompt('Copy this link to share', url); }
    }
  };
  return (
    <section id="showcase" className="space-y-5" data-testid="hackathon-showcase">
      <div className="flex items-center justify-between gap-3 pb-2 border-b-2 border-[#0A0A0A]">
        <h2 className="text-lg font-bold text-[#0A0A0A] uppercase tracking-[0.08em] flex items-center gap-2"><Trophy size={18} className="text-[#F59E0B]" /> Winners & Projects</h2>
        <button onClick={share} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#002FA7]" data-testid="share-showcase-btn"><Share2 size={14} /> Share</button>
      </div>
      {data.winners.length > 0 && <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{data.winners.map((w, i) => <WinnerCard key={w.team_id + w.label} w={w} i={i} />)}</div>}
      {data.projects.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3">
          {data.projects.map((t) => (
            <div key={t.id} className="bg-white border border-[#E4E4E7] rounded-lg p-4" data-testid={`showcase-project-${t.id}`}>
              <p className="font-bold text-[#0A0A0A]">{t.project.title}</p>
              <p className="text-xs text-[#A1A1AA]">Team {t.name}</p>
              {t.project.description && <p className="text-sm text-[#52525B] mt-1.5 line-clamp-2">{t.project.description}</p>}
              <Links p={t.project} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
